import { ActionReducerMapBuilder, createAsyncThunk } from '@reduxjs/toolkit';
import { findThingInRecipe } from '@kemu-io/kemu-core/common/recipeCache';
import { Position, RecipeWidget, WidgetType } from '@kemu-io/kemu-core/types';
import { cloneObj } from '@kemu-io/kemu-core/common';
import { LogicMapperState } from '../logicMapperSlice';
import { defineGroupFieldVariables } from './widgetGroupHelpers';
import { initializeAddedWidgets } from './common/widgetInitialization';
import * as recipeActions from '@src/app/recipe/utils';
import { StatelessRecipeWidget } from '@src/types/core_t';
import { getStatelessWidget } from '@common/utils';
import { RootState } from '@src/app/store';

type DuplicateWidgetActionPayload = {
  /** the id of the recipe in the pool */
	recipeId: string;
	/** the id of the Thing in the recipe */
	thingRecipeId: string;
	/** id of the widget in the Thing to duplicate */
	widgetThingId: string;
}

/**
 * Calculates the position of a duplicated gate with respect to its sourceWidget
 * @param recipeId the id of the recipe in the pool
 * @param blockId the id of the block in the recipe
 * @param sourceWidget the gate used as a source for the copy
 */
const getClonedWidgetPosition = (recipeId: string, blockId: string, sourceWidget: RecipeWidget): Position => {
	const DEFAULT_GATE_HEIGHT = 54;
	const domEl = recipeActions.getEntityDomEl(recipeId, blockId, sourceWidget.id);

	// TODO: make sure the newly added gate won't be outside the viewport.
	if (domEl) {
		const rect = domEl.getBoundingClientRect();
		return {
			x: sourceWidget.canvas.position.x,
			y: sourceWidget.canvas.position.y + rect.height + 5
		};
	} else {
		// This should not happen!.. but just in case
		console.warn(`Dom id not found for gate '${sourceWidget.id}' in block '${blockId}' for recipe '${recipeId}'`);
		return {
			// Add them vertically
			x: sourceWidget.canvas.position.x,
			y: sourceWidget.canvas.position.y + DEFAULT_GATE_HEIGHT,
		};
	}
};

/**
 * Duplicates a widget. In the case of widget bundles, it 
 * will also create new tmp locations and add the bundle to the
 * recipe storage.
 */
const duplicateWidgetAction = createAsyncThunk(
  'LogicMapper/duplicateWidget',  async (
    payload: DuplicateWidgetActionPayload,
    thunkApi
) : Promise<StatelessRecipeWidget> => {
  const { user } = thunkApi.getState() as RootState;

  const targetThing = findThingInRecipe(payload.recipeId, payload.thingRecipeId);
  if (!targetThing) {
    throw new Error(`Could not find thing with id ${payload.thingRecipeId}`);
  }

  const sourceWidgetId = payload.widgetThingId;
  const sourceWidget = targetThing.gates[sourceWidgetId];
	if (!sourceWidget) { throw new Error(`Widget ${sourceWidgetId} not found in Thing ${payload.thingRecipeId}`); }
  const newWidgetId = recipeActions.generateUniqueWidgetId(targetThing);

  // Handle standard widgets
	// Clone everything, specially the 'state'
	const clonedWidget = cloneObj<RecipeWidget>(sourceWidget);
	clonedWidget.id = newWidgetId;
	clonedWidget.canvas.position = getClonedWidgetPosition(payload.recipeId, payload.thingRecipeId, sourceWidget);
	clonedWidget.children = [];

  if (clonedWidget.type === WidgetType.widgetGroup) {
    defineGroupFieldVariables(clonedWidget);
  }

	targetThing.gates[newWidgetId] = clonedWidget;

  // initialize any widget that requires it
  await initializeAddedWidgets({
    recipeId: payload.recipeId,
    thingId: payload.thingRecipeId,
    addedWidgetsList: [clonedWidget],
    thunkApi,
    userId: user.currentUserProfile.profile?.id
  });

  const statelessClone = getStatelessWidget(clonedWidget);
  return statelessClone;

});

const duplicateWidgetReducer = ((builder: ActionReducerMapBuilder<LogicMapperState>): void => {
  builder.addCase(duplicateWidgetAction.fulfilled, (state, action) => {
    const clonedWidget = action.payload;
    state.gates[clonedWidget.id] = clonedWidget;
  });

  builder.addCase(duplicateWidgetAction.rejected, (_draft, action) => {
    console.error('Failed to clone widget: ', action.error);
  });
});

export default {
  duplicateWidgetAction,
  duplicateWidgetReducer,
};
